import {type AxiosRequestConfig} from 'axios';

import {spotheroGet} from 'api';
import {type ApiResponse} from 'api/api.types';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';

import {
    type CompPassBatchLinkDetail,
    type CompPassBatchLinkDetailsResults,
    type CompPassBatchPool,
    type CompPassBatchPoolsResponse,
    type CompPassBatchPoolsResults,
} from 'api/comp-passes/comp-pass.types';

interface SortingParams {
    page: number;
    perPage: number;
    orderBy?: string;
}

export interface GetCompPassBatchLinkDetailsParams {
    facilityId: number;
    batchId: number;
    params: SortingParams;
    config?: AxiosRequestConfig;
}

const formatRequestParams = (params: SortingParams) =>
    convertKeysToSnakeCase(params);

const formatPool = ({
    claimed,
    eventDetails,
    id,
    maxPasses,
    redeemed,
    timezone,
    transientDetails,
    url,
    usage,
}: CompPassBatchPool): CompPassBatchLinkDetail => {
    const details = eventDetails ?? transientDetails;

    return {
        compPassBatchLinkId: id,
        title:
            eventDetails?.eventTitle ?? transientDetails?.timeframeLabel ?? '',
        venue: eventDetails?.destinationTitle,
        passStarts: details?.starts ?? '',
        passEnds: details?.ends ?? '',
        timezone,
        claimedPasses: claimed,
        availablePasses: maxPasses,
        redeemedPasses: redeemed,
        entries: usage?.enter ?? null,
        exits: usage?.exit ?? null,
        sharedLinkUrl: url,
    };
};

const formatResponse = (
    response: ApiResponse<CompPassBatchPoolsResponse>
): CompPassBatchLinkDetailsResults => {
    const formattedResponse: CompPassBatchPoolsResults = camelizeKeys(
        response.data
    );

    return {
        ...formattedResponse,
        data: {
            results: formattedResponse.data.results.map(formatPool),
        },
    };
};

/**
 * Fetches shared-link details for a comp pass batch.
 *
 * @see '/api/v1/facilities/{facilityId}/comp-pass/batches/{batchId}/pools/'
 */
export const getCompPassBatchLinkDetails = async ({
    facilityId,
    batchId,
    params,
    config = {},
}: GetCompPassBatchLinkDetailsParams) => {
    try {
        const formattedParams = formatRequestParams(params);

        const response = await spotheroGet<CompPassBatchPoolsResponse>(
            `facilities/${facilityId}/comp-pass/batches/${batchId}/pools/`,
            formattedParams,
            config
        );

        return formatResponse(response);
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};
